import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { CalendarIcon, MapPin, GraduationCap, X } from 'lucide-react';
import { format } from 'date-fns';
import { createTutorProfile } from '../../services/authApi';
import { useToast } from '../../hooks/use-toast';

export const CreateTutorProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectInput, setSubjectInput] = useState('');
  const [teachingModes, setTeachingModes] = useState([]);
  const [spokenLanguages, setSpokenLanguages] = useState([]);
  const [languageInput, setLanguageInput] = useState('');
  const [teachingCategories, setTeachingCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [educationHistory, setEducationHistory] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [achievementInput, setAchievementInput] = useState('');
  const [awards, setAwards] = useState([]);
  const [awardInput, setAwardInput] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    dob: '',
    gender: '',
    qualification: '',
    experience_years: 0,
    hourly_rate: 400,
    monthly_rate: null,
    salary_type: 'hourly',
    bio: '',
    address: '',
    city: '',
    state: '',
    intro_video_url: '',
    latitude: null,
    longitude: null
  });

  const teachingModeOptions = [
    { value: 'online', label: 'Online' },
    { value: 'offline_tutor_place', label: 'At Tutor\'s Place' },
    { value: 'offline_student_place', label: 'At Student\'s Place' },
    { value: 'nearby_classes', label: 'Nearby Classes' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setFormData({ ...formData, dob: format(selectedDate, 'yyyy-MM-dd') });
  };

  const addSubject = () => {
    if (subjectInput.trim() && !subjects.includes(subjectInput.trim())) {
      setSubjects([...subjects, subjectInput.trim()]);
      setSubjectInput('');
    }
  };

  const removeSubject = (subject) => {
    setSubjects(subjects.filter(s => s !== subject));
  };

  const toggleTeachingMode = (mode) => {
    if (teachingModes.includes(mode)) {
      setTeachingModes(teachingModes.filter(m => m !== mode));
    } else {
      setTeachingModes([...teachingModes, mode]);
    }
  };


  const addLanguage = () => {
    if (languageInput.trim() && !spokenLanguages.includes(languageInput.trim())) {
      setSpokenLanguages([...spokenLanguages, languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang) => {
    setSpokenLanguages(spokenLanguages.filter(l => l !== lang));
  };

  const addCategory = () => {
    if (categoryInput.trim() && !teachingCategories.includes(categoryInput.trim())) {
      setTeachingCategories([...teachingCategories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const removeCategory = (cat) => {
    setTeachingCategories(teachingCategories.filter(c => c !== cat));
  };

  const addEducation = () => {
    const newEdu = {
      degree: '',
      institution: '',
      year: ''
    };
    setEducationHistory([...educationHistory, newEdu]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...educationHistory];
    updated[index][field] = value;
    setEducationHistory(updated);
  };

  const removeEducation = (index) => {
    setEducationHistory(educationHistory.filter((_, i) => i !== index));
  };

  const addAchievement = () => {
    if (achievementInput.trim()) {
      setAchievements([...achievements, achievementInput.trim()]);
      setAchievementInput('');
    }
  };

  const removeAchievement = (index) => {
    setAchievements(achievements.filter((_, i) => i !== index));
  };

  const addAward = () => {
    if (awardInput.trim()) {
      setAwards([...awards, awardInput.trim()]);
      setAwardInput('');
    }
  };

  const removeAward = (index) => {
    setAwards(awards.filter((_, i) => i !== index));
  };


  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          toast({
            title: "Location fetched!",
            description: "Your current location has been captured.",
          });
        },
        (error) => {
          toast({
            title: "Location error",
            description: "Unable to fetch your location.",
            variant: "destructive",
          });
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (subjects.length === 0) {
      toast({
        title: "Add subjects",
        description: "Please add at least one subject you can teach.",
        variant: "destructive",
      });
      return;
    }

    if (teachingModes.length === 0) {
      toast({
        title: "Select teaching mode",
        description: "Please select at least one teaching mode.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const profileData = {
        ...formData,
        subjects,
        teaching_mode: teachingModes,
        spoken_languages: spokenLanguages,
        teaching_category: teachingCategories,
        educational_history: educationHistory,
        achievements,
        awards,
        experience_years: parseInt(formData.experience_years),
        hourly_rate: formData.hourly_rate ? parseInt(formData.hourly_rate) : null,
        monthly_rate: formData.monthly_rate ? parseInt(formData.monthly_rate) : null
      };

      await createTutorProfile(profileData);
      
      toast({
        title: "Profile Created!",
        description: "Your tutor profile is under review. You'll be notified once verified.",
      });
      
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'tutor') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-purple-600 p-3 rounded-lg">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Create Tutor Profile</CardTitle>
                <CardDescription>Share your expertise and start teaching</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label>Date of Birth *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          initialFocus
                          disabled={(date) => date > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Professional Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="qualification">Qualification *</Label>
                    <Input
                      id="qualification"
                      name="qualification"
                      placeholder="e.g., M.Sc Mathematics, B.Ed"
                      value={formData.qualification}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="experience_years">Experience (Years) *</Label>
                    <Input
                      id="experience_years"
                      name="experience_years"
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="hourly_rate">Hourly Rate (₹)</Label>
                    <Input
                      id="hourly_rate"
                      name="hourly_rate"
                      type="number"
                      min="100"
                      step="50"
                      value={formData.hourly_rate}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="monthly_rate">Monthly Rate (₹)</Label>
                    <Input
                      id="monthly_rate"
                      name="monthly_rate"
                      type="number"
                      min="1000"
                      step="500"
                      value={formData.monthly_rate}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="salary_type">Salary Type *</Label>
                    <select
                      id="salary_type"
                      name="salary_type"
                      value={formData.salary_type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="hourly">Hourly</option>
                      <option value="monthly">Monthly</option>
                      <option value="both">Both</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Intro Video */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Intro Video (Optional)</h3>
                <Label htmlFor="intro_video_url">YouTube or Google Drive Link</Label>
                <Input
                  id="intro_video_url"
                  name="intro_video_url"
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={formData.intro_video_url}
                  onChange={handleChange}
                />
                <p className="text-sm text-gray-500 mt-1">Upload your video to YouTube or Drive first, then paste the public link here</p>
              </div>

              {/* Spoken Languages */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Spoken Languages</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Enter language (e.g., English, Hindi)"
                    value={languageInput}
                    onChange={(e) => setLanguageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                  />
                  <Button type="button" onClick={addLanguage}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {spokenLanguages.map((lang, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm">
                      {lang}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeLanguage(lang)} />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Teaching Category */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Teaching Category</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="e.g., Primary School, CBSE, Competitive Exams"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                  />
                  <Button type="button" onClick={addCategory}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {teachingCategories.map((cat, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm">
                      {cat}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeCategory(cat)} />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Educational History */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Educational History (Optional)</h3>
                <Button type="button" onClick={addEducation} variant="outline" className="mb-3">
                  + Add Education
                </Button>
                <div className="space-y-4">
                  {educationHistory.map((edu, idx) => (
                    <Card key={idx} className="p-4">
                      <div className="grid md:grid-cols-3 gap-3">
                        <Input
                          placeholder="Degree (e.g., B.Ed)"
                          value={edu.degree}
                          onChange={(e) => updateEducation(idx, 'degree', e.target.value)}
                        />
                        <Input
                          placeholder="Institution"
                          value={edu.institution}
                          onChange={(e) => updateEducation(idx, 'institution', e.target.value)}
                        />
                        <Input
                          placeholder="Year"
                          value={edu.year}
                          onChange={(e) => updateEducation(idx, 'year', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEducation(idx)}
                        className="mt-2 text-red-600"
                      >
                        Remove
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Achievements (Optional)</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Enter an achievement"
                    value={achievementInput}
                    onChange={(e) => setAchievementInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
                  />
                  <Button type="button" onClick={addAchievement}>Add</Button>
                </div>
                <ul className="space-y-2">
                  {achievements.map((achievement, idx) => (
                    <li key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span>{achievement}</span>
                      <X className="h-4 w-4 cursor-pointer text-red-600" onClick={() => removeAchievement(idx)} />
                    </li>
                  ))}
                </ul>
              </div>

              {/* Awards */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Awards (Optional)</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Enter an award"
                    value={awardInput}
                    onChange={(e) => setAwardInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAward())}
                  />
                  <Button type="button" onClick={addAward}>Add</Button>
                </div>
                <ul className="space-y-2">
                  {awards.map((award, idx) => (
                    <li key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span>{award}</span>
                      <X className="h-4 w-4 cursor-pointer text-red-600" onClick={() => removeAward(idx)} />
                    </li>
                  ))}
                </ul>
              </div>

              {/* Subjects */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Subjects You Teach *</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Enter subject (e.g., Class 10 Math, Physics)"
                    value={subjectInput}
                    onChange={(e) => setSubjectInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                  />
                  <Button type="button" onClick={addSubject}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {subjects.map((subject, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm">
                      {subject}
                      <X
                        className="h-3 w-3 ml-2 cursor-pointer"
                        onClick={() => removeSubject(subject)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Teaching Mode */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Teaching Mode *</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {teachingModeOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => toggleTeachingMode(option.value)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        teachingModes.includes(option.value)
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <p className="font-medium">{option.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">About You *</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Tell students about your teaching experience, methodology, and achievements..."
                  rows={4}
                  value={formData.bio}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Location */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Location</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={getCurrentLocation}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Fetch Current Location
                  </Button>
                  
                  {formData.latitude && formData.longitude && (
                    <p className="text-sm text-green-600">
                      Location captured: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? 'Creating Profile...' : 'Create Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
